import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Wrench, ClipboardCheck, Package, AlertTriangle, Calendar,
  AlertCircle, Bell, Boxes, TrendingUp, BarChart3, ArrowRight
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, AreaChart, Area, CartesianGrid
} from 'recharts';

const COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#be185d', '#4f46e5'];

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [pendingIssuesCount, setPendingIssuesCount] = useState(0);
  const [myAssignments, setMyAssignments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [reportFormData, setReportFormData] = useState({ issue_type: '', description: '' });

  useEffect(() => {
    if (user?.role === 'collaborator' && user?.linked_collaborator_id) {
      loadMyAssignments();
    } else {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      const [statsRes, issuesRes, advancedRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tool-issues/pending/count`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/dashboard/advanced-stats`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPendingIssuesCount(issuesRes.data.count);
      if (advancedRes.data) setAdvancedStats(advancedRes.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyAssignments = async () => {
    try {
      const { data } = await axios.get(`${API}/collaborators/${user.linked_collaborator_id}/assignments`);
      setMyAssignments(data);
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const openReportDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setReportFormData({ issue_type: '', description: '' });
    setReportDialogOpen(true);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAssignment || !reportFormData.issue_type) return;
    try {
      await axios.post(`${API}/tool-issues`, {
        assignment_id: selectedAssignment.id,
        issue_type: reportFormData.issue_type,
        description: reportFormData.description || null,
      });
      toast.success("Signalement envoyé à l'administrateur");
      setReportDialogOpen(false);
      setSelectedAssignment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du signalement");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  // Collaborator view
  if (user?.role === 'collaborator' && myAssignments) {
    return (
      <div className="p-8 space-y-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">Mes Outils</h1>
          <p className="text-muted-foreground mt-2">Bienvenue {myAssignments.collaborator?.name}, voici vos outils affectés</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Outils Actifs</CardTitle>
              <div className="p-2 rounded-lg bg-chart-3/10"><Wrench className="w-4 h-4 text-chart-3" /></div>
            </CardHeader>
            <CardContent><div className="text-3xl font-bold font-heading">{myAssignments.active_assignments?.length || 0}</div></CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <h2 className="text-2xl font-heading font-bold">Outils en ma possession</h2>
          {myAssignments.active_assignments?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myAssignments.active_assignments.map((assignment) => {
                const daysRemaining = getDaysRemaining(assignment.end_date);
                const isExpiring = daysRemaining !== null && daysRemaining <= 7;
                const hasPendingIssue = assignment.has_pending_issue;
                return (
                  <Card key={assignment.id} data-testid={`my-tool-${assignment.id}`}
                    className={`hover:shadow-lg transition-shadow duration-200 
                      ${hasPendingIssue ? 'bg-rose-100 border-rose-300 border-2' : ''}
                      ${isExpiring && !hasPendingIssue ? 'border-destructive border-2' : ''}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {assignment.tool?.photo ? (
                            <img src={assignment.tool.photo} alt={assignment.tool.name} className={`w-12 h-12 object-contain rounded-lg ${hasPendingIssue ? 'bg-rose-50' : 'bg-muted'}`} />
                          ) : (
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${hasPendingIssue ? 'bg-rose-50' : 'bg-muted'}`}><Package className={`w-6 h-6 ${hasPendingIssue ? 'text-rose-400' : 'text-muted-foreground'}`} /></div>
                          )}
                          <div>
                            <CardTitle className="text-lg font-heading flex items-center gap-2">{assignment.tool?.name}<Badge variant="secondary">x{assignment.quantity}</Badge></CardTitle>
                            <p className="text-sm text-muted-foreground">{assignment.tool?.type}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          {hasPendingIssue && (
                            <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Signalé
                            </Badge>
                          )}
                          {isExpiring && !hasPendingIssue && <Badge variant="destructive">Expire bientôt</Badge>}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {hasPendingIssue && (
                        <div className="p-2 bg-rose-100 rounded-lg text-rose-700 text-sm flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span>En cours de traitement par l'administrateur</span>
                        </div>
                      )}
                      <div className="text-xs font-mono text-muted-foreground">N° {assignment.tool?.article_number || 'N/A'}</div>
                      <div className="text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4" /><span>Affecté le: {formatDate(assignment.assigned_date)}</span></div>
                        {assignment.end_date && (
                          <div className="flex items-center gap-2 text-muted-foreground mt-1"><AlertTriangle className="w-4 h-4" /><span>Retour prévu: {formatDate(assignment.end_date)}</span></div>
                        )}
                      </div>
                      {daysRemaining !== null && (
                        <Badge variant={daysRemaining > 7 ? 'outline' : 'destructive'}>{daysRemaining > 0 ? `${daysRemaining} jour(s) restant(s)` : 'Expiré'}</Badge>
                      )}
                      {!hasPendingIssue && (
                        <Button data-testid={`report-issue-${assignment.id}`} onClick={() => openReportDialog(assignment)} variant="outline" size="sm" className="w-full rounded-full mt-2 border-orange-500 text-orange-500 hover:bg-orange-50">
                          <AlertCircle className="w-4 h-4 mr-2" /> Signaler un problème
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Wrench className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucun outil ne vous est affecté actuellement</p>
              </CardContent>
            </Card>
          )}
        </div>
        <Dialog open={reportDialogOpen} onOpenChange={(open) => { setReportDialogOpen(open); if (!open) { setSelectedAssignment(null); setReportFormData({ issue_type: '', description: '' }); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Signaler un problème</DialogTitle></DialogHeader>
            {selectedAssignment && (
              <form onSubmit={handleReportSubmit} className="space-y-4">
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p className="text-lg font-heading font-medium">{selectedAssignment.tool?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedAssignment.tool?.type}</p>
                  <Badge variant="secondary">Quantité: {selectedAssignment.quantity}</Badge>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issue_type">Type de problème *</Label>
                  <Select value={reportFormData.issue_type} onValueChange={(v) => setReportFormData({ ...reportFormData, issue_type: v })}>
                    <SelectTrigger id="issue_type" data-testid="issue-type-select" className="rounded-lg"><SelectValue placeholder="Sélectionner le type de problème" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lost">Perdu</SelectItem>
                      <SelectItem value="broken">Cassé</SelectItem>
                      <SelectItem value="worn">Usé (besoin de remplacement)</SelectItem>
                      <SelectItem value="leasing_end">Fin de leasing (à retourner au fournisseur)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnel)</Label>
                  <Textarea id="description" data-testid="issue-description-input" value={reportFormData.description} onChange={(e) => setReportFormData({ ...reportFormData, description: e.target.value })} placeholder="Décrivez le problème en détail..." className="rounded-lg" />
                </div>
                <Button data-testid="submit-issue-report" type="submit" className="w-full rounded-full" disabled={!reportFormData.issue_type}>Envoyer le signalement</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Admin/Manager view
  const statCards = [
    { title: 'Collaborateurs', value: stats?.total_collaborators || 0, icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-600/10' },
    { title: 'Outils Total', value: stats?.total_tools || 0, icon: Wrench, color: 'text-violet-600', bgColor: 'bg-violet-600/10' },
    { title: 'Affectations Actives', value: stats?.active_assignments || 0, icon: ClipboardCheck, color: 'text-emerald-600', bgColor: 'bg-emerald-600/10' },
    { title: 'Outils Disponibles', value: stats?.available_tools || 0, icon: Package, color: 'text-amber-600', bgColor: 'bg-amber-600/10' },
    { title: 'Expirent Bientôt', value: stats?.expiring_soon || 0, icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-600/10' },
  ];

  const pieData = advancedStats ? [
    { name: 'Assignés', value: advancedStats.stock_status.assigned },
    { name: 'Disponibles', value: advancedStats.stock_status.available },
  ] : [];

  const statusLabel = (s) => ({ active: 'Actif', returned: 'Retourné', tool_deleted: 'Supprimé' }[s] || s);

  return (
    <div data-testid="admin-dashboard" className="p-8 space-y-8">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">Tableau de bord</h1>
        <p className="text-muted-foreground mt-2">Vue d'ensemble de votre inventaire</p>
      </div>

      {/* Pending issues alert */}
      {pendingIssuesCount > 0 && (
        <Card className="border-orange-500 border-2 bg-orange-50 dark:bg-orange-950/20 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/tool-issues')} data-testid="pending-issues-alert">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-500/20"><Bell className="w-6 h-6 text-orange-500" /></div>
              <div>
                <h3 className="text-lg font-heading font-bold text-orange-700 dark:text-orange-400">{pendingIssuesCount} Signalement{pendingIssuesCount > 1 ? 's' : ''} en attente</h3>
                <p className="text-sm text-orange-600 dark:text-orange-300">Des collaborateurs ont signalé des problèmes avec leurs outils</p>
              </div>
            </div>
            <Button variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-100" onClick={(e) => { e.stopPropagation(); navigate('/tool-issues'); }}>Voir les signalements</Button>
          </CardContent>
        </Card>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card key={i} data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`} className="hover:shadow-lg transition-shadow duration-200">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.title}</CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}><Icon className={`w-4 h-4 ${stat.color}`} /></div>
              </CardHeader>
              <CardContent><div className="text-3xl font-bold font-heading">{stat.value}</div></CardContent>
            </Card>
          );
        })}
      </div>

      {/* Advanced Stats Section */}
      {advancedStats && (
        <>
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="stat-utilization-rate">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Taux d'utilisation</p>
                    <p className="text-2xl font-bold font-heading">{advancedStats.utilization_rate}%</p>
                  </div>
                  <div className="p-2 rounded-lg bg-emerald-600/10"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-total-kits">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Kits créés</p>
                    <p className="text-2xl font-bold font-heading">{advancedStats.total_kits}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-violet-600/10"><Boxes className="w-5 h-5 text-violet-600" /></div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-active-rentals">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Locations actives</p>
                    <p className="text-2xl font-bold font-heading">{advancedStats.active_rentals}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-amber-600/10"><Package className="w-5 h-5 text-amber-600" /></div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-pending-issues">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Signalements en attente</p>
                    <p className="text-2xl font-bold font-heading">{advancedStats.pending_issues}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-red-600/10"><AlertCircle className="w-5 h-5 text-red-600" /></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stock Status Pie */}
            <Card data-testid="chart-stock-status">
              <CardHeader><CardTitle className="text-base font-heading">Répartition du stock</CardTitle></CardHeader>
              <CardContent>
                {pieData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={4} strokeWidth={0}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${v} unité(s)`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">Aucune donnée</div>
                )}
                <div className="flex justify-center gap-6 mt-2">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                      <span className="text-muted-foreground">{d.name}: <span className="font-medium text-foreground">{d.value}</span></span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tools by type bar chart */}
            <Card data-testid="chart-tools-by-type">
              <CardHeader><CardTitle className="text-base font-heading">Outils par catégorie</CardTitle></CardHeader>
              <CardContent>
                {advancedStats.tools_by_type.length > 0 ? (
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={advancedStats.tools_by_type.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="type" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip formatter={(v) => `${v} unité(s)`} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[230px] flex items-center justify-center text-muted-foreground text-sm">Aucune donnée</div>
                )}
              </CardContent>
            </Card>

            {/* Assignments trend area chart */}
            <Card data-testid="chart-assignments-trend">
              <CardHeader><CardTitle className="text-base font-heading">Affectations (30 jours)</CardTitle></CardHeader>
              <CardContent>
                {advancedStats.assignments_trend.some(d => d.count > 0) ? (
                  <ResponsiveContainer width="100%" height={230}>
                    <AreaChart data={advancedStats.assignments_trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip labelFormatter={(l) => `Date: ${l}`} formatter={(v) => [`${v} affectation(s)`]} />
                      <Area type="monotone" dataKey="count" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[230px] flex items-center justify-center text-muted-foreground text-sm">Aucune affectation récente</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Collaborators */}
            <Card data-testid="top-collaborators">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-heading">Top Collaborateurs</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/collaborators')} className="text-xs"><BarChart3 className="w-3 h-3 mr-1" /> Voir tous</Button>
              </CardHeader>
              <CardContent>
                {advancedStats.top_collaborators.length > 0 ? (
                  <div className="space-y-3">
                    {advancedStats.top_collaborators.map((c, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">{i + 1}</div>
                          <span className="font-medium text-sm">{c.name}</span>
                        </div>
                        <Badge variant="secondary">{c.tools_count} outil{c.tools_count > 1 ? 's' : ''}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">Aucune affectation active</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Assignments */}
            <Card data-testid="recent-assignments">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-heading">Dernières affectations</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/assignments')} className="text-xs"><ArrowRight className="w-3 h-3 mr-1" /> Voir toutes</Button>
              </CardHeader>
              <CardContent>
                {advancedStats.recent_assignments.length > 0 ? (
                  <div className="space-y-2">
                    {advancedStats.recent_assignments.slice(0, 6).map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium truncate block">{a.tool_name}</span>
                          <span className="text-xs text-muted-foreground">{a.collaborator_name} - x{a.quantity}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant={a.status === 'active' ? 'default' : 'outline'} className="text-xs">{statusLabel(a.status)}</Badge>
                          <span className="text-xs text-muted-foreground">{formatShortDate(a.date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">Aucune affectation récente</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
